import _omit from "lodash/omit";
import * as React from 'react';
import ReactDOM from 'react-dom';
import Textarea from 'react-textarea-autosize';
import i18n from '@signavio/i18n';
import { defaultStyle } from '@signavio/ui';
import { utils } from '../styles';
import Markdown, { escape } from './Markdown';
import UserGuideLink from './UserGuideLink';
import { Hint } from './hints';
import { Tab, TabBar } from './tabs';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
class MarkdownInput extends React.Component {
  state = {
    showPreview: false
  };
  constructor(props) {
    super(props);
    this.content = null;
  }
  getSnapshotBeforeUpdate(_, prevState) {
    if (this.state.showPreview && !prevState.showPreview && this.content) {
      return ReactDOM.findDOMNode(this.content).offsetHeight;
    }
    return null;
  }
  componentDidUpdate(_, __, snapshot) {
    if (!snapshot) {
      return;
    }
    this.setState({
      previewHeight: snapshot
    });
  }
  render() {
    const {
      showPreview
    } = this.state;
    const {
      style,
      readOnly,
      description
    } = this.props;
    if (readOnly) {
      return this.renderPreview();
    }
    return /*#__PURE__*/_jsxs("div", {
      ...style,
      children: [/*#__PURE__*/_jsx("div", {
        ...style('tabbar'),
        children: /*#__PURE__*/_jsxs(TabBar, {
          children: [/*#__PURE__*/_jsx(Tab, {
            small: true,
            active: !showPreview,
            onClick: () => this.setState({
              showPreview: false
            }),
            children: i18n('Write')
          }, "edit"), /*#__PURE__*/_jsx(Tab, {
            small: true,
            active: showPreview,
            onClick: () => this.setState({
              showPreview: true
            }),
            children: i18n('Preview')
          }, "preview")]
        })
      }), this.renderInput(), /*#__PURE__*/_jsxs("div", {
        ...style('footer'),
        children: [description && /*#__PURE__*/_jsx("div", {
          ...style('description'),
          children: description
        }), /*#__PURE__*/_jsx("div", {
          children: i18n('You can use __link__ for formatting.', {
            link: /*#__PURE__*/_jsx(UserGuideLink, {
              chapter: "markdown",
              children: "Markdown"
            })
          })
        })]
      })]
    });
  }
  renderInput() {
    const {
      showPreview = 0
    } = this.state;
    const {
      children,
      style,
      value,
      ...rest
    } = this.props;
    if (showPreview) {
      return /*#__PURE__*/_jsx("div", {
        ...style('preview'),
        children: this.renderPreview()
      });
    }
    if (children) {
      return /*#__PURE__*/_jsx("div", {
        ...style('content'),
        ref: ref => this.content = ref,
        children: children
      });
    }
    const {
      style: inlineStyles,
      ...stylingAttrs
    } = style(['content', 'textarea']);
    return /*#__PURE__*/_jsx(Textarea, {
      ..._omit(rest, 'renderPreview'),
      ...stylingAttrs,
      ref: ref => this.content = ref,
      minRows: 5,
      style: inlineStyles,
      value: value
    });
  }
  renderPreview() {
    const {
      renderPreview,
      value,
      defaultValue,
      readOnly
    } = this.props;
    let finalValue;
    if (renderPreview) {
      finalValue = renderPreview(escape(value || defaultValue));
    } else {
      finalValue = value || defaultValue;
    }
    if (!finalValue) {
      return /*#__PURE__*/_jsx(Hint, {
        children: i18n('Nothing to preview')
      });
    }
    const {
      previewHeight = 0
    } = this.state;
    return /*#__PURE__*/_jsx(Markdown, {
      dataTestId: "preview",
      style: {
        minHeight: readOnly ? 0 : Math.max(100, previewHeight)
      },
      unsafe: !!renderPreview,
      children: finalValue
    });
  }
}
const styled = defaultStyle(({
  padding,
  font,
  color
}) => ({
  textAlign: 'left',
  footer: {
    fontSize: font.size.small,
    ...utils.border('1px', 'solid', color.mono.lighter),
    borderTop: 'none',
    backgroundColor: color.mono.ultralight,
    paddingLeft: padding.normal,
    paddingRight: padding.normal,
    paddingTop: padding.xsmall,
    paddingBottom: padding.xsmall
  },
  tabbar: {
    position: 'relative',
    zIndex: 1,
    marginBottom: -1
  },
  preview: {
    padding: padding.normal,
    fontSize: font.size.button,
    ...utils.border('1px', 'solid', color.mono.lighter)
  },
  description: {
    marginBottom: padding.xsmall
  },
  textarea: {
    width: '100%',
    verticalAlign: 'top',
    ...utils.border('1px', 'solid', color.mono.lighter),
    ...utils.transition(['border-color', 'box-shadow'], '0.15s'),
    ':focus': {
      border: `1px solid ${color.primary.base}`
    }
  }
}));
export default styled(MarkdownInput);